import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Custom hook for handling tab navigation with keyboard shortcuts and URL persistence
 * @param {Object} options - Configuration options
 * @param {string[]} options.mainTabs - Array of main tab names
 * @param {Object} options.subTabs - Object mapping main tabs to their sub-tabs
 * @param {string} options.defaultMainTab - Default main tab to show
 * @param {string} options.defaultSubTab - Default sub-tab to show
 * @param {Function} options.onTabChange - Optional callback when tabs change
 * @returns {Object} Tab state and handlers
 */
const useTabNavigation = ({
   mainTabs,
   subTabs = {},
   defaultMainTab,
   defaultSubTab,
   onTabChange
}) => {
   const navigate = useNavigate();
   const location = useLocation();

   // Parse tab state from URL or use defaults
   const [activeMainTab, setActiveMainTab] = useState(() => {
      const params = new URLSearchParams(location.search);
      return params.get('tab') || defaultMainTab;
   });

   const [activeSubTab, setActiveSubTab] = useState(() => {
      const params = new URLSearchParams(location.search);
      return params.get('subtab') || defaultSubTab || activeMainTab;
   });

   // Handle tab changes
   const handleMainTabChange = (tab) => {
      setActiveMainTab(tab);
      // Only reset sub-tab if there are sub-tabs for this main tab
      if (subTabs[tab]?.length > 0) {
         setActiveSubTab(subTabs[tab][0]); // Reset sub-tab when main tab changes
      } else {
         setActiveSubTab(tab); // Use main tab as sub-tab when there are no sub-tabs
      }
      onTabChange?.(); // Optional callback
   };

   const handleSubTabChange = (tab) => {
      setActiveSubTab(tab);
      onTabChange?.(); // Optional callback
   };

   // Keyboard navigation
   useEffect(() => {
      const handleKeyPress = (e) => {
         if (e.altKey) {
            switch (e.key) {
               case 'ArrowRight':
               case 'ArrowLeft':
               case 'ArrowUp':
               case 'ArrowDown':
                  // Prevent default browser navigation
                  e.preventDefault();

                  if (e.key === 'ArrowRight') {
                     const currentMainIndex = mainTabs.indexOf(activeMainTab);
                     if (currentMainIndex < mainTabs.length - 1) {
                        handleMainTabChange(mainTabs[currentMainIndex + 1]);
                     }
                  } else if (e.key === 'ArrowLeft') {
                     const prevMainIndex = mainTabs.indexOf(activeMainTab);
                     if (prevMainIndex > 0) {
                        handleMainTabChange(mainTabs[prevMainIndex - 1]);
                     }
                  } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                     // Only handle up/down if there are sub-tabs for the current main tab
                     const currentSubTabs = subTabs[activeMainTab];
                     if (currentSubTabs?.length > 0) {
                        const currentSubIndex = currentSubTabs.indexOf(activeSubTab);
                        if (e.key === 'ArrowDown' && currentSubIndex < currentSubTabs.length - 1) {
                           handleSubTabChange(currentSubTabs[currentSubIndex + 1]);
                        } else if (e.key === 'ArrowUp' && currentSubIndex > 0) {
                           handleSubTabChange(currentSubTabs[currentSubIndex - 1]);
                        }
                     }
                  }
                  break;
            }
         }
      };

      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
   }, [activeMainTab, activeSubTab, mainTabs, subTabs]);

   // Update URL when tabs change
   useEffect(() => {
      const params = new URLSearchParams(location.search);
      params.set('tab', activeMainTab);
      // Only set subtab if there are sub-tabs
      if (Object.keys(subTabs).length > 0) {
         params.set('subtab', activeSubTab);
      }
      navigate({
         pathname: location.pathname,
         search: params.toString()
      }, { replace: true });
   }, [activeMainTab, activeSubTab, navigate, location.pathname]);

   return {
      activeMainTab,
      activeSubTab,
      handleMainTabChange,
      handleSubTabChange
   };
};

export default useTabNavigation;